<template>
  <div class="solutions">
    <UnifiedNavBar />
    
    <!-- 页面头部 -->
    <section class="page-header">
      <div class="container">
        <h1 class="page-title">垂直解决方案</h1>
        <p class="page-subtitle">基于行业深度理解，为不同领域客户提供专业化、定制化的数字化转型解决方案</p>
      </div>
    </section>

    <!-- 方案导航区 -->
    <section class="solutions-nav">
      <div class="container">
        <div class="nav-cards">
          <div class="nav-card" @click="scrollToSection('smart-manufacturing')">
            <div class="nav-icon">🏭</div>
            <h3>智能制造解决方案</h3>
            <p>制造业、矿山、能源、化工等传统工业</p>
          </div>
          <div class="nav-card" @click="scrollToSection('digital-procurement')">
            <div class="nav-icon">🛒</div>
            <h3>数字化采购解决方案</h3>
            <p>大型集团企业、供应链管理企业</p>
          </div>
          <div class="nav-card" @click="scrollToSection('smart-terminal')">
            <div class="nav-icon">📱</div>
            <h3>智能终端解决方案</h3>
            <p>矿山、石油、化工等危险作业环境</p>
          </div>
        </div>
      </div>
    </section>

    <!-- 智能制造解决方案 -->
    <section id="smart-manufacturing" class="solution-section">
      <div class="container">
        <div class="solution-header">
          <h2 class="solution-title">智能制造解决方案</h2>
          <p class="solution-subtitle">构建数字化工厂管理体系，推动传统制造业向智能化转型升级</p>
        </div>
        
        <div class="solution-content">
          <div class="solution-overview">
            <div class="overview-image">
              <img src="https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Smart%20manufacturing%20factory%20with%20AI%20automation%20robotic%20systems%20digital%20twin%20technology%20modern%20industrial%20blue%20theme&image_size=landscape_4_3" alt="智能制造解决方案">
            </div>
            <div class="overview-content">
              <h3>方案概述</h3>
              <p>通过物联网、人工智能、大数据等先进技术，实现生产设备的智能化管理，构建从设备监控到生产优化的全流程数字化体系。</p>
              <div class="core-values">
                <div class="value-item">
                  <span class="value-number">30%+</span>
                  <span class="value-label">生产效率提升</span>
                </div>
                <div class="value-item">
                  <span class="value-number">降低</span>
                  <span class="value-label">安全事故发生率</span>
                </div>
                <div class="value-item">
                  <span class="value-number">预测性</span>
                  <span class="value-label">设备维护</span>
                </div>
              </div>
            </div>
          </div>

          <div class="solution-products">
            <h3>核心产品组合</h3>
            <div class="products-grid">
              <div class="product-card">
                <div class="product-icon">🤖</div>
                <h4>矿山行业人工智能场景</h4>
                <p>基于AI技术的智能识别、预测分析和自动化控制系统</p>
              </div>
              <div class="product-card">
                <div class="product-icon">🏗️</div>
                <h4>智慧工程管控系统</h4>
                <p>全流程工程项目管理和智能化施工监控平台</p>
              </div>
              <div class="product-card">
                <div class="product-icon">🔗</div>
                <h4>工业互联网平台</h4>
                <p>设备连接、数据采集、智能分析的一体化平台</p>
              </div>
              <div class="product-card">
                <div class="product-icon">⚙️</div>
                <h4>设备运维平台</h4>
                <p>设备健康监测、故障预警和维护管理系统</p>
              </div>
            </div>
          </div>

          <div class="solution-cases">
            <h3>经典案例</h3>
            <div class="cases-grid">
              <div class="case-card">
                <div class="case-header">
                  <h4>某大型钢铁企业智能化改造</h4>
                  <span class="case-industry">钢铁冶金</span>
                </div>
                <p class="case-desc">通过部署工业物联网平台和AI分析系统，实现了全流程的智能化监控和管理，显著提升了生产效率和产品质量。</p>
                <div class="case-results">
                  <div class="result-item">
                    <div class="result-value">32%</div>
                    <div class="result-metric">生产效率提升</div>
                  </div>
                  <div class="result-item">
                    <div class="result-value">25%</div>
                    <div class="result-metric">能耗降低</div>
                  </div>
                  <div class="result-item">
                    <div class="result-value">40%</div>
                    <div class="result-metric">故障率下降</div>
                  </div>
                </div>
              </div>
              <div class="case-card">
                <div class="case-header">
                  <h4>化工园区安全监控系统</h4>
                  <span class="case-industry">石油化工</span>
                </div>
                <p class="case-desc">建设了覆盖全园区的安全监控物联网系统，实现了危险源的实时监控和预警，大幅提升了安全管理水平。</p>
                <div class="case-results">
                  <div class="result-item">
                    <div class="result-value">100%</div>
                    <div class="result-metric">安全事故预防</div>
                  </div>
                  <div class="result-item">
                    <div class="result-value">50%</div>
                    <div class="result-metric">应急响应时间缩短</div>
                  </div>
                  <div class="result-item">
                    <div class="result-value">30%</div>
                    <div class="result-metric">安全管理成本降低</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 数字化采购解决方案 -->
    <section id="digital-procurement" class="solution-section alt-bg">
      <div class="container">
        <div class="solution-header">
          <h2 class="solution-title">数字化采购解决方案</h2>
          <p class="solution-subtitle">构建阳光采购体系，实现供应链透明化管理和成本优化</p>
        </div>
        
        <div class="solution-content">
          <div class="solution-overview">
            <div class="overview-content">
              <h3>方案概述</h3>
              <p>基于云计算和大数据技术，打造集采购管理、供应商管理、电子商务于一体的数字化采购平台，实现采购全流程的数字化转型。</p>
              <div class="core-values">
                <div class="value-item">
                  <span class="value-number">15-20%</span>
                  <span class="value-label">采购成本降低</span>
                </div>
                <div class="value-item">
                  <span class="value-number">50%+</span>
                  <span class="value-label">采购效率提高</span>
                </div>
                <div class="value-item">
                  <span class="value-number">透明化</span>
                  <span class="value-label">供应链管理</span>
                </div>
              </div>
            </div>
            <div class="overview-image">
              <img src="https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Digital%20procurement%20platform%20with%20supply%20chain%20management%20e-commerce%20dashboard%20data%20analytics%20modern%20business%20green%20theme&image_size=landscape_4_3" alt="数字化采购解决方案">
            </div>
          </div>

          <div class="solution-products">
            <h3>核心产品组合</h3>
            <div class="products-grid">
              <div class="product-card">
                <div class="product-icon">🛒</div>
                <h4>SRM物资集采平台</h4>
                <p>集中采购管理、供应商协同、采购流程优化的一体化平台</p>
              </div>
              <div class="product-card">
                <div class="product-icon">☁️</div>
                <h4>山能云商平台</h4>
                <p>B2B电子商务平台，实现在线交易和供应链协同</p>
              </div>
              <div class="product-card">
                <div class="product-icon">👥</div>
                <h4>供应商管理系统</h4>
                <p>供应商准入、评估、绩效管理的全生命周期管理</p>
              </div>
              <div class="product-card">
                <div class="product-icon">📊</div>
                <h4>采购数据分析平台</h4>
                <p>采购数据挖掘、成本分析、决策支持系统</p>
              </div>
            </div>
          </div>

          <div class="solution-cases">
            <h3>经典案例</h3>
            <div class="cases-grid">
              <div class="case-card">
                <div class="case-header">
                  <h4>某大型能源集团采购数字化</h4>
                  <span class="case-industry">能源行业</span>
                </div>
                <p class="case-desc">通过部署SRM物资集采平台，实现了集团内部采购的统一管理和供应商的协同合作，大幅降低了采购成本。</p>
                <div class="case-results">
                  <div class="result-item">
                    <div class="result-value">18%</div>
                    <div class="result-metric">采购成本降低</div>
                  </div>
                  <div class="result-item">
                    <div class="result-value">60%</div>
                    <div class="result-metric">采购效率提升</div>
                  </div>
                  <div class="result-item">
                    <div class="result-value">95%</div>
                    <div class="result-metric">供应商满意度</div>
                  </div>
                </div>
              </div>
              <div class="case-card">
                <div class="case-header">
                  <h4>制造业集团供应链优化</h4>
                  <span class="case-industry">制造业</span>
                </div>
                <p class="case-desc">建设了覆盖全集团的数字化采购平台，实现了供应链的透明化管理和采购流程的标准化。</p>
                <div class="case-results">
                  <div class="result-item">
                    <div class="result-value">22%</div>
                    <div class="result-metric">库存成本降低</div>
                  </div>
                  <div class="result-item">
                    <div class="result-value">45%</div>
                    <div class="result-metric">采购周期缩短</div>
                  </div>
                  <div class="result-item">
                    <div class="result-value">100%</div>
                    <div class="result-metric">采购流程数字化</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 智能终端解决方案 -->
    <section id="smart-terminal" class="solution-section">
      <div class="container">
        <div class="solution-header">
          <h2 class="solution-title">智能终端解决方案</h2>
          <p class="solution-subtitle">保障作业人员安全，构建智慧安全管理体系</p>
        </div>
        
        <div class="solution-content">
          <div class="solution-overview">
            <div class="overview-image">
              <img src="https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Smart%20terminal%20devices%20for%20industrial%20safety%20explosion%20proof%20mobile%20phones%20wearable%20devices%20mining%20environment%20orange%20theme&image_size=landscape_4_3" alt="智能终端解决方案">
            </div>
            <div class="overview-content">
              <h3>方案概述</h3>
              <p>专为危险作业环境设计的智能终端设备和安全管理系统，实现作业人员的实时通信、定位监控和健康管理。</p>
              <div class="core-values">
                <div class="value-item">
                  <span class="value-number">保障</span>
                  <span class="value-label">作业人员安全</span>
                </div>
                <div class="value-item">
                  <span class="value-number">实时</span>
                  <span class="value-label">通信和定位</span>
                </div>
                <div class="value-item">
                  <span class="value-number">健康</span>
                  <span class="value-label">监测功能</span>
                </div>
              </div>
            </div>
          </div>

          <div class="solution-products">
            <h3>核心产品组合</h3>
            <div class="products-grid">
              <div class="product-card">
                <div class="product-icon">📱</div>
                <h4>矿用本安型手机系列</h4>
                <p>防爆认证的智能手机，支持井下通信和应急救援</p>
              </div>
              <div class="product-card">
                <div class="product-icon">⌚</div>
                <h4>矿用本安型手表</h4>
                <p>可穿戴设备，实时监测生命体征和环境参数</p>
              </div>
              <div class="product-card">
                <div class="product-icon">📡</div>
                <h4>工业防爆通信设备</h4>
                <p>专业的防爆通信终端和基站设备</p>
              </div>
              <div class="product-card">
                <div class="product-icon">🛡️</div>
                <h4>安全监测终端</h4>
                <p>环境监测、人员定位、应急报警的综合终端</p>
              </div>
            </div>
          </div>

          <div class="solution-cases">
            <h3>经典案例</h3>
            <div class="cases-grid">
              <div class="case-card">
                <div class="case-header">
                  <h4>某大型煤矿智慧安全系统</h4>
                  <span class="case-industry">煤炭行业</span>
                </div>
                <p class="case-desc">部署了全套智能终端设备和安全管理系统，实现了井下人员的实时监控和应急救援，大幅提升了矿山安全水平。</p>
                <div class="case-results">
                  <div class="result-item">
                    <div class="result-value">100%</div>
                    <div class="result-metric">人员实时定位</div>
                  </div>
                  <div class="result-item">
                    <div class="result-value">80%</div>
                    <div class="result-metric">应急响应时间缩短</div>
                  </div>
                  <div class="result-item">
                    <div class="result-value">0</div>
                    <div class="result-metric">重大安全事故</div>
                  </div>
                </div>
              </div>
              <div class="case-card">
                <div class="case-header">
                  <h4>石化企业安全监控平台</h4>
                  <span class="case-industry">石油化工</span>
                </div>
                <p class="case-desc">建设了覆盖全厂区的智能终端监控网络，实现了危险区域作业人员的全方位安全保障。</p>
                <div class="case-results">
                  <div class="result-item">
                    <div class="result-value">24/7</div>
                    <div class="result-metric">实时监控</div>
                  </div>
                  <div class="result-item">
                    <div class="result-value">90%</div>
                    <div class="result-metric">事故预防率</div>
                  </div>
                  <div class="result-item">
                    <div class="result-value">35%</div>
                    <div class="result-metric">安全管理效率提升</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 统计数据 -->
    <section class="statistics">
      <div class="container">
        <div class="stats-grid">
          <div class="stat-item">
            <div class="stat-number">500+</div>
            <div class="stat-label">成功案例</div>
          </div>
          <div class="stat-item">
            <div class="stat-number">50+</div>
            <div class="stat-label">行业覆盖</div>
          </div>
          <div class="stat-item">
            <div class="stat-number">1000+</div>
            <div class="stat-label">企业客户</div>
          </div>
          <div class="stat-item">
            <div class="stat-number">99.9%</div>
            <div class="stat-label">系统可用性</div>
          </div>
        </div>
      </div>
    </section>

    <!-- 联系咨询 -->
    <section class="contact-section">
      <div class="container">
        <div class="contact-content">
          <h2>开启数字化转型之旅</h2>
          <p>专业的解决方案团队为您提供定制化的数字化转型服务</p>
          <button class="contact-btn">立即咨询</button>
        </div>
      </div>
    </section>

    <Footer />
  </div>
</template>

<script>
import UnifiedNavBar from '@/components/UnifiedNavBar.vue'
import Footer from '@/components/Footer.vue'

export default {
  name: 'Solutions',
  components: {
    UnifiedNavBar,
    Footer
  },
  methods: {
    scrollToSection(sectionId) {
      const element = document.getElementById(sectionId)
      if (element) {
        element.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        })
      }
    }
  }
}
</script>

<style scoped>
/* 页面头部样式 */
.page-header {
  background: linear-gradient(135deg, #0052cc 0%, #1e90ff 100%);
  color: white;
  padding: 120px 0 80px;
  text-align: center;
}

.page-title {
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

.page-subtitle {
  font-size: 1.2rem;
  max-width: 800px;
  margin: 0 auto;
  opacity: 0.9;
}

/* 方案导航区样式 */
.solutions-nav {
  padding: 80px 0;
  background: #f8f9fa;
}

.nav-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
}

.nav-card {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 4px 15px rgba(0, 82, 204, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
}

.nav-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0, 82, 204, 0.2);
}

.nav-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.nav-card h3 {
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #2c3e50;
}

.nav-card p {
  color: #666;
  font-size: 0.9rem;
}

/* 解决方案区域样式 */
.solution-section {
  padding: 80px 0;
}

.solution-section.alt-bg {
  background: #f8f9fa;
}

.solution-header {
  text-align: center;
  margin-bottom: 3rem;
}

.solution-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #2c3e50;
}

.solution-subtitle {
  font-size: 1.1rem;
  color: #666;
  max-width: 800px;
  margin: 0 auto;
}

/* 方案概述样式 */
.solution-overview {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  margin-bottom: 3rem;
}

.overview-image img {
  width: 100%;
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(0, 82, 204, 0.15);
}

.overview-content h3 {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #2c3e50;
}

.overview-content p {
  color: #666;
  line-height: 1.6;
  margin-bottom: 2rem;
}

.core-values {
  display: flex;
  gap: 2rem;
}

.value-item {
  text-align: center;
}

.value-number {
  display: block;
  font-size: 1.5rem;
  font-weight: 700;
  color: #0052cc;
  margin-bottom: 0.5rem;
}

.value-label {
  font-size: 0.9rem;
  color: #666;
}

/* 产品组合样式 */
.solution-products {
  margin-bottom: 3rem;
}

.solution-products h3 {
  font-size: 1.8rem;
  font-weight: 600;
  margin-bottom: 2rem;
  color: #2c3e50;
  text-align: center;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
}

.product-card {
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 82, 204, 0.1);
  transition: all 0.3s ease;
}

.product-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 15px rgba(0, 82, 204, 0.15);
}

.product-icon {
  font-size: 2rem;
  margin-bottom: 1rem;
}

.product-card h4 {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #2c3e50;
}

.product-card p {
  color: #666;
  font-size: 0.9rem;
  line-height: 1.5;
}

/* 案例样式 */
.solution-cases h3 {
  font-size: 1.8rem;
  font-weight: 600;
  margin-bottom: 2rem;
  color: #2c3e50;
  text-align: center;
}

.cases-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;
}

.case-card {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 82, 204, 0.1);
  transition: all 0.3s ease;
}

.case-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0, 82, 204, 0.2);
}

.case-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.case-header h4 {
  font-size: 1.2rem;
  font-weight: 600;
  color: #2c3e50;
}

.case-industry {
  background: #0052cc;
  color: white;
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
}

.case-desc {
  color: #666;
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

.case-results {
  display: flex;
  justify-content: space-between;
}

.result-item {
  text-align: center;
}

.result-value {
  display: block;
  font-size: 1.3rem;
  font-weight: 700;
  color: #0052cc;
}

.result-metric {
  font-size: 0.8rem;
  color: #666;
}

/* 统计数据样式 */
.statistics {
  padding: 80px 0;
  background: linear-gradient(135deg, #0052cc 0%, #1e90ff 100%);
  color: white;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  text-align: center;
}

.stat-number {
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.stat-label {
  font-size: 1.1rem;
  opacity: 0.9;
}

/* 联系咨询样式 */
.contact-section {
  padding: 80px 0;
  background: #f8f9fa;
  text-align: center;
}

.contact-content h2 {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #2c3e50;
}

.contact-content p {
  font-size: 1.1rem;
  color: #666;
  margin-bottom: 2rem;
}

.contact-btn {
  background: linear-gradient(135deg, #0052cc 0%, #1e90ff 100%);
  color: white;
  border: none;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.contact-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 82, 204, 0.3);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .page-title {
    font-size: 2rem;
  }
  
  .page-subtitle {
    font-size: 1rem;
  }
  
  .solution-title {
    font-size: 2rem;
  }
  
  .solution-overview {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
  
  .nav-cards,
  .products-grid,
  .cases-grid {
    grid-template-columns: 1fr;
  }
  
  .core-values {
    flex-direction: column;
    gap: 1rem;
  }
  
  .case-results {
    flex-direction: column;
    gap: 1rem;
  }
  
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>